import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../contexts/I18nContext';
import { opportunitiesAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Search, Filter, MapPin, TrendingUp, Loader2, Download } from 'lucide-react';

const OpportunitiesPage = () => {
  const { formatCurrency } = useI18n();
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingSlug, setDownloadingSlug] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    projectType: 'all',
    status: 'all',
  });

  useEffect(() => {
    fetchOpportunities();
  }, [filters.projectType, filters.status]);

  const handleDownloadPdf = async (e, slug) => {
    e.preventDefault();
    e.stopPropagation();
    setDownloadingSlug(slug);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/opportunities/${slug}/pdf`);
      if (!response.ok) throw new Error('Error descargando PDF');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `silver_brick_${slug}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading PDF:', error);
    } finally {
      setDownloadingSlug(null);
    }
  };

  const fetchOpportunities = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.projectType !== 'all') params.project_type = filters.projectType;
      if (filters.status !== 'all') params.status = filters.status;
      
      const response = await opportunitiesAPI.list(params);
      // Map API response to expected format (API returns flat structure)
      const mapped = (response.data.opportunities || []).map(opp => ({
        opportunity_id: opp.opportunity_id,
        title: opp.title || opp.title_es || 'Sin título',
        slug: opp.slug,
        project_type: opp.project_type,
        investment_structure: opp.investment_structure,
        summary: opp.summary || opp.summary_es || '',
        location_city: opp.location_city || '',
        location_state: opp.location_state || '',
        capital_target: opp.capital_target || 0,
        minimum_investment: opp.minimum_investment || 0,
        funding_progress: opp.funding_progress || 0,
        projected_irr_base: opp.projected_irr_base || null,
        projected_irr_upside: opp.projected_irr_upside || null,
        risk_level: opp.risk_level || 'medium',
        status: opp.status,
        is_featured: opp.is_featured || false,
        image: opp.media_gallery?.[0] || 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800'
      }));
      setOpportunities(mapped);
    } catch (error) {
      console.error('Error fetching opportunities:', error);
      setOpportunities([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredOpportunities = opportunities.filter(opp => {
    const title = opp.title || '';
    const city = opp.location_city || '';
    const searchLower = filters.search.toLowerCase();
    return title.toLowerCase().includes(searchLower) ||
           city.toLowerCase().includes(searchLower);
  });

  const projectTypes = [
    { value: 'all', label: 'Todos los Tipos' },
    { value: 'residential', label: 'Residencial' },
    { value: 'commercial', label: 'Comercial' },
    { value: 'industrial', label: 'Industrial' },
    { value: 'mixed_use', label: 'Uso Mixto' },
    { value: 'land', label: 'Terreno' },
  ];

  const statusOptions = [
    { value: 'all', label: 'Todos los Estados' },
    { value: 'open', label: 'Abierto' },
    { value: 'soft_circle', label: 'Soft Circle' },
  ];

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
      soft_circle: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
      fully_funded: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
      closed: 'bg-muted text-muted-foreground',
    };
    const labels = {
      open: 'Abierto',
      soft_circle: 'Soft Circle',
      fully_funded: 'Completado',
      closed: 'Cerrado',
    };
    return (
      <Badge variant="outline" className={styles[status] || styles.closed}>
        {labels[status] || status}
      </Badge>
    );
  };

  // Sample data for demo
  const sampleOpportunities = [
    {
      opportunity_id: '1',
      title: 'Torre Reforma Elite',
      slug: 'torre-reforma-elite',
      project_type: 'mixed_use',
      investment_structure: 'SPV',
      summary: 'Desarrollo de torre de uso mixto de 35 pisos en Paseo de la Reforma con oficinas premium y espacios comerciales.',
      location_city: 'Ciudad de México',
      location_state: 'CDMX',
      capital_target: 250000000,
      minimum_investment: 500000,
      funding_progress: 65,
      projected_irr_base: 18,
      projected_irr_upside: 22,
      risk_level: 'medium',
      status: 'open',
      is_featured: true,
      image: 'https://images.unsplash.com/photo-1579283987703-5ae978f95ace?q=80&w=2574&auto=format&fit=crop'
    },
    {
      opportunity_id: '2',
      title: 'Industrial Park Querétaro',
      slug: 'industrial-park-queretaro',
      project_type: 'industrial',
      investment_structure: 'Club Deal',
      summary: 'Parque industrial clase A con contratos de arrendamiento triple-net y anclas internacionales.',
      location_city: 'Querétaro',
      location_state: 'Querétaro',
      capital_target: 180000000,
      minimum_investment: 250000,
      funding_progress: 45,
      projected_irr_base: 14,
      projected_irr_upside: 18,
      risk_level: 'low',
      status: 'open',
      is_featured: true,
      image: 'https://images.pexels.com/photos/1707823/pexels-photo-1707823.jpeg'
    },
    {
      opportunity_id: '3',
      title: 'Residencial Bosques Premium',
      slug: 'residencial-bosques-premium',
      project_type: 'residential',
      investment_structure: 'Equity',
      summary: 'Desarrollo residencial de lujo en Bosques de las Lomas con 48 unidades exclusivas.',
      location_city: 'Ciudad de México',
      location_state: 'CDMX',
      capital_target: 120000000,
      minimum_investment: 350000,
      funding_progress: 80,
      projected_irr_base: 16,
      projected_irr_upside: 20,
      risk_level: 'medium',
      status: 'soft_circle',
      is_featured: false,
      image: 'https://images.unsplash.com/photo-1671722294182-ed01cbe66bd1?q=80&w=2670&auto=format&fit=crop'
    },
  ];

  const displayOpportunities = filteredOpportunities;

  return (
    <div className="min-h-screen bg-background pt-20">
      {/* Header */}
      <section className="section-spacing border-b border-border">
        <div className="container-app">
          <div className="max-w-3xl">
            <p className="caption mb-4">Inversión</p>
            <h1 className="heading-1 mb-6">Oportunidades de Inversión</h1>
            <p className="body-lg">
              Explore nuestras oportunidades de inversión inmobiliaria cuidadosamente estructuradas y revisadas por nuestro equipo.
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 border-b border-border sticky top-20 bg-background/95 backdrop-blur-sm z-30">
        <div className="container-app">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
              <Input
                placeholder="Buscar por nombre o ubicación..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select
              value={filters.projectType}
              onValueChange={(value) => setFilters(prev => ({ ...prev, projectType: value }))}
            >
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Tipo de proyecto" />
              </SelectTrigger>
              <SelectContent>
                {projectTypes.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.status}
              onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
            >
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Opportunities Grid */}
      <section className="section-spacing">
        <div className="container-app">
          {loading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" strokeWidth={1.5} />
            </div>
          ) : displayOpportunities.length === 0 ? (
            <div className="text-center py-24">
              <p className="text-muted-foreground">No se encontraron oportunidades</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {displayOpportunities.map((opp) => (
                <Link 
                  key={opp.opportunity_id} 
                  to={`/opportunities/${opp.slug}`}
                  className="group"
                  data-testid={`opportunity-card-${opp.slug}`}
                >
                  <div className="card-premium p-0 overflow-hidden h-full flex flex-col">
                    <div className="aspect-[4/3] relative overflow-hidden">
                      <img 
                        src={opp.image || opp.media_gallery?.[0] || 'https://images.unsplash.com/photo-1579281959510-26b99ae23ae3?q=80&w=2574&auto=format&fit=crop'}
                        alt={opp.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent" />
                      <div className="absolute top-4 left-4 flex gap-2">
                        {getStatusBadge(opp.status)}
                        {opp.is_featured && (
                          <Badge className="bg-primary text-primary-foreground">
                            Destacado
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    <div className="p-6 flex-1 flex flex-col">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                        <MapPin className="h-3 w-3" strokeWidth={1.5} />
                        {opp.location_city}, {opp.location_state}
                      </div>
                      
                      <h3 className="text-xl font-medium mb-3 group-hover:text-primary transition-colors">
                        {opp.title}
                      </h3>
                      
                      <p className="text-sm text-muted-foreground mb-4 line-clamp-2 flex-1">
                        {opp.summary}
                      </p>
                      
                      {/* Funding Progress */}
                      <div className="mb-4">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-muted-foreground">Avance de fondeo</span>
                          <span className="text-foreground font-medium">{opp.funding_progress}%</span>
                        </div>
                        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-primary rounded-full transition-all"
                            style={{ width: `${opp.funding_progress}%` }}
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Inversión Mínima</p>
                          <p className="font-medium">{formatCurrency(opp.minimum_investment)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground mb-1">TIR Objetivo</p>
                          <p className="font-medium text-primary flex items-center justify-end gap-1">
                            <TrendingUp className="h-3 w-3" strokeWidth={1.5} />
                            {opp.projected_irr_base && opp.projected_irr_upside 
                              ? `${opp.projected_irr_base}-${opp.projected_irr_upside}%`
                              : 'Por definir'}
                          </p>
                        </div>
                      </div>

                      {/* Download PDF Button */}
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="w-full mt-4 gap-2 relative z-10"
                        onClick={(e) => handleDownloadPdf(e, opp.slug)}
                        disabled={downloadingSlug === opp.slug}
                        data-testid={`download-pdf-${opp.slug}`}
                      >
                        {downloadingSlug === opp.slug ? (
                          <>
                            <Loader2 className="h-3 w-3 animate-spin" strokeWidth={1.5} />
                            Generando...
                          </>
                        ) : (
                          <>
                            <Download className="h-3 w-3" strokeWidth={1.5} />
                            Descargar PDF
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 border-t border-border">
        <div className="container-app">
          <p className="text-xs text-muted-foreground/60 text-center max-w-3xl mx-auto">
            Los rendimientos proyectados son estimaciones basadas en análisis de mercado y no garantizan resultados futuros. La inversión en bienes raíces conlleva riesgos, incluyendo la posible pérdida de capital. Consulte los documentos de cada oportunidad para información completa sobre riesgos y términos.
          </p>
        </div>
      </section>
    </div>
  );
};

export default OpportunitiesPage;
